import { useEffect, useState, useCallback } from 'react'
import { Ticket, Check, Undo2, Trash2 } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { raffleAPI } from '@/lib/api'

const fmt = (iso) => {
  if (!iso) return ''
  const d = new Date(iso)
  return d.toLocaleString('nl-BE', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })
}

/* Admin-only widget op user-profiel: zie & mark tickets als "ingediend"
   wanneer de speler ter plaatse komt inruilen. */
export default function RaffleAdminWidget({ userId }) {
  const [data, setData] = useState(null)
  const [err, setErr] = useState(null)
  const [busyId, setBusyId] = useState(null)

  const laad = useCallback(async () => {
    try {
      const r = await raffleAPI.adminListForUser(userId)
      setData(r.data)
      setErr(null)
    } catch (e) {
      if (e.response?.status === 403) setErr('disabled')
      else setErr(e.response?.data?.error || e.message)
    }
  }, [userId])

  useEffect(() => { laad() }, [laad])

  const inruilen = async (id) => {
    setBusyId(id)
    try {
      await raffleAPI.adminRedeem(id)
      await laad()
    } catch (e) {
      window.alert(e.response?.data?.error || e.message)
    } finally { setBusyId(null) }
  }

  const ongedaan = async (id) => {
    if (!window.confirm('Ticket terug op "open" zetten?')) return
    setBusyId(id)
    try {
      await raffleAPI.adminUnredeem(id)
      await laad()
    } catch (e) {
      window.alert(e.response?.data?.error || e.message)
    } finally { setBusyId(null) }
  }

  const wisTicket = async (id) => {
    if (!window.confirm(`Ticket #${id} definitief verwijderen? Kan niet ongedaan gemaakt worden.`)) return
    setBusyId(id)
    try {
      await raffleAPI.adminDeleteTicket(id)
      await laad()
    } catch (e) {
      window.alert(e.response?.data?.error || e.message)
    } finally { setBusyId(null) }
  }

  const wisAlles = async () => {
    const n = data?.tickets?.length ?? 0
    if (n === 0) return
    if (!window.confirm(`Alle ${n} tickets van deze speler in de huidige editie definitief verwijderen? Onomkeerbaar.`)) return
    setBusyId('all')
    try {
      const r = await raffleAPI.adminDeleteAllForUser(userId)
      await laad()
      window.alert(`${r.data?.deleted ?? 0} tickets gewist.`)
    } catch (e) {
      window.alert(e.response?.data?.error || e.message)
    } finally { setBusyId(null) }
  }

  if (err === 'disabled') return null

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <CardTitle className="flex items-center gap-2 text-base">
            <Ticket className="h-4 w-4" style={{ color: '#E4C265' }} />
            Raffle-tickets
            <span className="pf-mono text-[10px] uppercase tracking-widest text-muted-foreground ml-1">admin</span>
          </CardTitle>
          {(data?.tickets?.length ?? 0) > 0 && (
            <Button size="sm" variant="ghost"
                    disabled={busyId === 'all'}
                    onClick={wisAlles}
                    className="text-signal hover:bg-signal/10 h-7 text-xs">
              <Trash2 className="h-3 w-3 mr-1" /> Alle wissen
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {!data && <div className="text-xs text-muted-foreground">Laden…</div>}
        {err && err !== 'disabled' && <div className="text-xs text-destructive">{err}</div>}
        {data && (
          <>
            <div className="flex items-baseline gap-4">
              <div>
                <div className="text-2xl font-bold" style={{ color: '#E4C265' }}>{data.counts?.open ?? 0}</div>
                <div className="pf-mono text-[10px] uppercase tracking-widest text-muted-foreground">Open</div>
              </div>
              <div>
                <div className="text-lg font-semibold text-muted-foreground">{data.counts?.used ?? 0}</div>
                <div className="pf-mono text-[10px] uppercase tracking-widest text-muted-foreground">Ingediend</div>
              </div>
            </div>
            {data.tickets?.length === 0 && (
              <div className="text-xs text-muted-foreground">Deze speler heeft nog geen tickets verdiend.</div>
            )}
            <div className="space-y-1">
              {data.tickets?.slice(0, 20).map(t => (
                <div key={t.id} className="flex items-center gap-2 text-xs rounded-[4px] border border-border px-2 py-1.5">
                  <span className="pf-mono text-muted-foreground w-8">#{t.id}</span>
                  <span className="flex-1 truncate">{fmt(t.granted_at)}</span>
                  {t.used_at ? (
                    <>
                      <span className="pf-mono text-[10px] uppercase text-muted-foreground">
                        ingediend {fmt(t.used_at)}
                      </span>
                      <Button size="sm" variant="ghost"
                              disabled={busyId === t.id}
                              onClick={() => ongedaan(t.id)}>
                        <Undo2 className="h-3 w-3" />
                      </Button>
                    </>
                  ) : (
                    <Button size="sm" variant="outline"
                            disabled={busyId === t.id}
                            onClick={() => inruilen(t.id)}>
                      <Check className="h-3 w-3 mr-1" />Inruilen
                    </Button>
                  )}
                  <Button size="sm" variant="ghost"
                          disabled={busyId === t.id}
                          onClick={() => wisTicket(t.id)}
                          className="text-signal hover:bg-signal/10 h-7 w-7 p-0"
                          title="Ticket definitief wissen">
                    <Trash2 className="h-3 w-3" />
                  </Button>
                </div>
              ))}
            </div>
          </>
        )}
      </CardContent>
    </Card>
  )
}
